import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { fetchPopupNotice, recordPopupEvent } from "../api/popupApi";

const PREFIX = "ckh_popup_v1";
const hoursForFrequency = { "6h": 6, "12h": 12, "24h": 24 };

const popupKey = (popup) => `${PREFIX}:${popup.id}:${popup.version || "v"}`;

const canShowByFrequency = (popup) => {
  if (!popup) return false;
  const key = popupKey(popup);
  try {
    if (window.localStorage.getItem(`${key}:dismissed`) === "1") return false;
    if (popup.frequency === "always") return true;
    if (popup.frequency === "session") return window.sessionStorage.getItem(`${key}:shown`) !== "1";
    const hours = hoursForFrequency[popup.frequency];
    if (!hours) return false;
    const stored = window.localStorage.getItem(`${key}:shown-at`);
    if (stored === null || stored.trim() === "") return true;
    const lastShown = Number(stored);
    if (!Number.isFinite(lastShown) || lastShown <= 0 || lastShown > Date.now()) return true;
    return Date.now() - lastShown >= hours * 3600000;
  } catch (_error) {
    return true;
  }
};

const markShown = (popup) => {
  const key = popupKey(popup);
  try {
    if (popup.frequency === "session") window.sessionStorage.setItem(`${key}:shown`, "1");
    if (hoursForFrequency[popup.frequency]) window.localStorage.setItem(`${key}:shown-at`, String(Date.now()));
  } catch (_error) {}
};

const markDismissed = (popup) => {
  if (!popup?.dismissForever) return;
  try { window.localStorage.setItem(`${popupKey(popup)}:dismissed`, "1"); } catch (_error) {}
};

export default function usePopupNotice({ enabled, surface, device }) {
  const [popup, setPopup] = useState(null);
  const [visible, setVisible] = useState(false);
  const presentationRef = useRef(null);

  useEffect(() => {
    let disposed = false;
    setVisible(false);
    setPopup(null);
    presentationRef.current = null;
    if (!enabled || !surface || !device) return () => { disposed = true; };
    const controller = new AbortController();
    let timer = null;
    fetchPopupNotice({ surface, device, signal: controller.signal })
      .then((nextPopup) => {
        if (disposed || controller.signal.aborted || !nextPopup || !canShowByFrequency(nextPopup)) return;
        setPopup(nextPopup);
        timer = window.setTimeout(() => {
          if (!disposed && !controller.signal.aborted) setVisible(true);
        }, nextPopup.delaySeconds * 1000);
      })
      .catch(() => {
        // Popup notices fail closed: no notice is safer than disrupting news.
      });
    return () => {
      disposed = true;
      controller.abort();
      if (timer !== null) window.clearTimeout(timer);
    };
  }, [enabled, surface, device]);

  useEffect(() => {
    if (!visible || !popup) return;
    const key = popupKey(popup);
    if (!presentationRef.current || presentationRef.current.key !== key) {
      presentationRef.current = { key, closed: false };
    }
    if (presentationRef.current.shown) return;
    presentationRef.current.shown = true;
    markShown(popup);
    recordPopupEvent({ popupId: popup.id, event: "shown", surface, device });
  }, [popup, visible, surface, device]);

  const close = useCallback(() => {
    if (!popup || !visible) return;
    markDismissed(popup);
    setVisible(false);
    if (presentationRef.current && !presentationRef.current.closed) {
      presentationRef.current.closed = true;
      recordPopupEvent({ popupId: popup.id, event: "closed", surface, device });
    }
  }, [popup, visible, surface, device]);

  const click = useCallback(() => {
    if (!popup) return;
    recordPopupEvent({ popupId: popup.id, event: "clicked", surface, device });
  }, [popup, surface, device]);

  return useMemo(() => ({ popup, visible: Boolean(popup && visible), close, click }), [popup, visible, close, click]);
}
